import { PageHeader, Section, Overline, PrimaryCTA } from "@/components/Section";
import { GitBranch, Database, Lock, FileCheck2, ShieldCheck, Scale, Flag } from "lucide-react";

const SECTIONS = [
  {
    icon: GitBranch,
    title: "Versioned methodology",
    body: "Continuum implements regulated methods — currently the federal HICC HNA template and the BC HNR method — as versioned, immutable modules. Adding a new province's template, or a new domain's methodology, is a new module; nothing else changes. A change to a regulated method results in a new method version; existing outputs keep their original version stamp so year-over-year comparability is engineered in, not asserted.",
  },
  {
    icon: Database,
    title: "Data sources & lineage",
    body: "Every indicator panel exposes its lineage: which source (Statistics Canada SDMX/WDS, the Census Profile, CMHC, HART Dataverse, CRA T3010, provincial registries, your data layer), which method version, and when it was last refreshed. The lineage view is one click from any number — including numbers in exported reports.",
  },
  {
    icon: FileCheck2,
    title: "Named accountability",
    body: "Statutory outputs (Housing Needs Reports, certified community profiles) carry a named professional signature. Not a logo, not a workflow stamp — a person whose name is registered against the output and reachable for follow-up.",
  },
  {
    icon: Scale,
    title: "Comparability across versions",
    body: "Year-over-year deltas carry the method version under which each year was computed. When the federal template changes, the previous version is preserved alongside the new one; comparisons footnote the methodology change rather than silently absorbing it. Auditors can reproduce any historical number on the version it was originally produced under.",
  },
  {
    icon: Lock,
    title: "Data residency & handling",
    body: "Hosted in Canadian regions. Your data layer stays yours: exportable on request, never used to train external models, never sold or shared with marketing networks. Role-based access controls available from the Municipal tier upward.",
  },
  {
    icon: ShieldCheck,
    title: "Security posture (current state)",
    body: "Encrypted in transit and at rest. Routine backup and incident response procedures documented. SOC 2 work is on the roadmap; we publish it as a roadmap item rather than implying it today. Honest current state is part of the methodology.",
  },
  {
    icon: Flag,
    title: "Honesty architecture & basis flags",
    body: "Continuum suppresses or nulls rather than fabricates. Data gaps are flagged loudly, never silently zero-filled. Every figure carries a basis flag — published · estimate · municipal · suppressed · computed — so a reader always knows whether a number came from a published source, a modelled estimate, or local administrative data. Suppression propagates through calculations; estimated values are flagged separately from census-sourced ones.",
  },
];

export default function Method() {
  return (
    <>
      <PageHeader
        overline="Method & trust"
        title="The page procurement evaluators read first."
        lede="Continuum is, structurally, a methodology document that learned to talk. Every claim on the rest of this site routes back here."
        testid="method-page-header"
      />

      <Section testid="method-sections" className="!pt-16">
        <div className="grid lg:grid-cols-12 gap-12">
          <aside className="lg:col-span-3">
            <div className="lg:sticky lg:top-24">
              <Overline className="mb-4">On this page</Overline>
              <ol className="space-y-3 text-slate-700 text-sm">
                {SECTIONS.map((s, i) => (
                  <li key={s.title} className="tabular-nums">
                    <span className="text-[#FF8A00] font-bold mr-2">0{i + 1}</span>
                    <a href={`#section-${i}`} className="hover:text-[#0A1224]">{s.title}</a>
                  </li>
                ))}
              </ol>
              <div className="mt-10 pt-8 border-t border-slate-200">
                <Overline className="mb-3">Living document</Overline>
                <p className="text-slate-600 text-sm leading-relaxed">
                  This page is the public face of the methodology. Method updates are
                  announced to the waitlist before they take effect.
                </p>
              </div>
            </div>
          </aside>

          <div className="lg:col-span-9 space-y-16">
            {SECTIONS.map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={s.title} id={`section-${i}`} data-testid={`method-section-${i}`} className="scroll-mt-24">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-10 h-10 bg-[#0A1224] flex items-center justify-center">
                      <Icon className="text-[#FF8A00]" size={20} />
                    </div>
                    <div className="text-xs font-black tabular-nums tracking-[0.2em] uppercase text-slate-500">
                      Section 0{i + 1}
                    </div>
                  </div>
                  <h2 className="text-3xl md:text-4xl font-black tracking-tighter text-[#0A1224] leading-tight mb-4">{s.title}</h2>
                  <p className="text-slate-700 text-lg leading-relaxed max-w-prose">{s.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </Section>

      <Section testid="method-cta" className="bg-[#0A1224] text-white text-center">
        <Overline className="text-slate-400 mb-6">Methodology drops</Overline>
        <h2 className="text-3xl md:text-5xl font-black tracking-tighter max-w-3xl mx-auto leading-tight">
          Get methodology versions in your inbox before they ship.
        </h2>
        <div className="mt-10">
          <PrimaryCTA to="/contact" testid="method-cta-button">Join the waitlist</PrimaryCTA>
        </div>
      </Section>
    </>
  );
}
